"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";
import { AnimatePresence, motion } from "motion/react";
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider as RHFFormProvider,
  useFormContext,
} from "react-hook-form";
import { cn } from "../lib/cn";
import { durations, easings } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { Label } from "../primitives/Label";

/**
 * FormField — the canonical shadcn react-hook-form + zod wrapper, ported into
 * `@aisquare/ui` as a namespaced, brand-themed a11y primitive.
 *
 * It collapses the per-field plumbing every form rebuilds by hand — the
 * `<form>` provider, the `Controller`, and the `id` / `aria-describedby` /
 * `aria-invalid` / label-`htmlFor` accessibility contract — into one set of
 * parts. A consumer writes a zod schema + fields and gets focus association,
 * error wiring, and a11y for free, with errors styled through the
 * `text-destructive` token (never a hand-coded hex).
 *
 * ──────────────────────────────────────────────────────────────────────────
 * SYMBOL COLLISION — why the exports are renamed.
 *
 * `@aisquare/ui` ALREADY exports `Form` and `FormField` from the blocks
 * barrel (`src/components/blocks/Form.tsx` — the K2 chat `form`-block
 * renderer, a different concern). To avoid clashing, this RHF wrapper exports:
 *
 *   donor name   →  exported here as
 *   ───────────────────────────────────
 *   Form         →  FormProvider          (the `Form = FormProvider` re-export)
 *   FormField    →  FormFieldController    (the Controller wrapper)
 *   FormItem        FormItem               (no collision — exported as-is)
 *   FormLabel       FormLabel
 *   FormControl     FormControl
 *   FormDescription FormDescription
 *   FormMessage     FormMessage
 *   useFormField    useFormField
 *
 * The public API mirrors the donor 1:1, so a consumer that wants the donor
 * names locally aliases on import:
 *
 *   import {
 *     FormProvider as Form,
 *     FormFieldController as FormField,
 *     FormItem, FormLabel, FormControl, FormDescription, FormMessage,
 *   } from "@aisquare/ui";
 *
 * ──────────────────────────────────────────────────────────────────────────
 * Usage:
 *
 *   const schema = z.object({ email: z.string().email("Enter a valid email") });
 *   const form = useForm<z.infer<typeof schema>>({
 *     resolver: zodResolver(schema),
 *     defaultValues: { email: "" },
 *   });
 *
 *   <FormProvider {...form}>
 *     <form onSubmit={form.handleSubmit(onValid)}>
 *       <FormFieldController
 *         control={form.control}
 *         name="email"
 *         render={({ field }) => (
 *           <FormItem>
 *             <FormLabel>Email</FormLabel>
 *             <FormControl>
 *               <Input type="email" {...field} />
 *             </FormControl>
 *             <FormDescription>We never share it.</FormDescription>
 *             <FormMessage />
 *           </FormItem>
 *         )}
 *       />
 *     </form>
 *   </FormProvider>
 *
 * react-hook-form, @hookform/resolvers, and zod are declared as (optional)
 * peerDependencies and externalized from the bundle, so consumers share ONE
 * RHF context instance and the library never ships a second copy.
 */

/**
 * `FormProvider` — the donor's `Form = FormProvider` re-export. A thin pass-
 * through of react-hook-form's `FormProvider` so descendant parts can read
 * form state via `useFormContext`. Renamed from `Form` to dodge the blocks
 * barrel's `Form` chat renderer.
 */
const FormProvider = RHFFormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
);

/**
 * `FormFieldController` — the donor's `FormField`. Generic over
 * `<TFieldValues, TName>`, spreads `ControllerProps` straight into RHF's
 * `Controller`, and publishes `{ name }` on `FormFieldContext` so descendant
 * parts (`FormLabel`, `FormControl`, `FormMessage`) can resolve this field's
 * state via `useFormField`. The render prop hands back `{ field, fieldState }`
 * fully typed against the consumer's zod schema. Renamed from `FormField` to
 * dodge the blocks barrel's `FormField` type/value.
 */
const FormFieldController = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
);

/**
 * `useFormField` — the heart of the a11y wiring. Reads `FormFieldContext` +
 * `FormItemContext`, resolves this field's state via `getFieldState`, and
 * returns the stable id scheme the parts wire onto the DOM: `formItemId`
 * (the control id + label `htmlFor`), `formDescriptionId`, `formMessageId`,
 * plus the spread `fieldState` (so `error` is available). Throws when used
 * outside a `FormFieldController`. The id scheme + throw are preserved
 * verbatim from the donor.
 */
const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

/**
 * `formItemVariants` — the only styling surface on the parts. `spacing`
 * controls the vertical rhythm between label / control / message so dense
 * forms (settings hub) and roomy forms (auth) share one component:
 *   - `default` : `space-y-2` (the donor default — roomy)
 *   - `tight`   : `space-y-1` (compact rows)
 */
export const formItemVariants = cva("", {
  variants: {
    spacing: {
      default: "space-y-2",
      tight: "space-y-1",
    },
  },
  defaultVariants: {
    spacing: "default",
  },
});

export interface FormItemProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof formItemVariants> {}

/**
 * `FormItem` — wraps one field's parts. Mints a unique `React.useId()` into
 * `FormItemContext` (this is what makes the label / control / message ids
 * unique per field instance) and lays the parts out with the `spacing`
 * variant.
 */
const FormItem = React.forwardRef<HTMLDivElement, FormItemProps>(
  ({ className, spacing, ...props }, ref) => {
    const id = React.useId();

    return (
      <FormItemContext.Provider value={{ id }}>
        <div ref={ref} className={cn(formItemVariants({ spacing }), className)} {...props} />
      </FormItemContext.Provider>
    );
  },
);
FormItem.displayName = "FormItem";

/**
 * `FormLabel` — composes the brand `Label` primitive (token-styled `<label>`
 * with `peer-disabled` handling) and adds the error color + `htmlFor`
 * association. On a validation error the label turns `text-destructive`;
 * `htmlFor={formItemId}` ties the click target to the control that
 * `FormControl` ids. Composes `Label` instead of re-adding
 * `@radix-ui/react-label`, matching every other field in the library.
 */
const FormLabel = React.forwardRef<
  React.ElementRef<typeof Label>,
  React.ComponentPropsWithoutRef<typeof Label>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      ref={ref}
      className={cn(error && "text-destructive", className)}
      htmlFor={formItemId}
      {...props}
    />
  );
});
FormLabel.displayName = "FormLabel";

/**
 * `FormControl` — the critical a11y contract, preserved verbatim. Injects
 * onto whatever control it wraps (brand `Input`, `Textarea`, `Select`,
 * `Checkbox`, `PasswordInput`, …): `id={formItemId}`, the right
 * `aria-describedby` (description only when valid; description + message when
 * errored), and `aria-invalid`. Paired with `Input`'s
 * `aria-invalid:border-destructive`, the control's border / ring morphs
 * idle → error → valid via its existing token transition, no consumer
 * wiring. Stays a `<Slot>`-style merge so it adds no styling of its own.
 */
const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={
        !error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
});
FormControl.displayName = "FormControl";

/**
 * `FormDescription` — the always-on helper text. Carries `formDescriptionId`
 * so `FormControl`'s `aria-describedby` points at it, styled
 * `text-muted-foreground`.
 */
const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
});
FormDescription.displayName = "FormDescription";

/**
 * `FormMessage` — the validation message. `error.message` takes precedence
 * over `children`, and the part returns `null` when there is no body (no
 * empty error node) — both preserved verbatim from the donor.
 *
 * Motion: the message fades + slides in on a small `y` offset
 * (`durations.hover` ≈ 180ms, the fast end of the lib presets) and exits
 * cleanly via `AnimatePresence`
 * when the field becomes valid, instead of a hard DOM pop. The error color
 * morph itself lives on the control's border via `aria-invalid`. Every
 * animation is gated on `useReducedMotion`: when reduced (or before the
 * client resolves the preference), the message appears / removes instantly
 * with no transform. `AnimatePresence` handles the unmount, so the
 * `return null` contract is preserved — when there is no body, nothing
 * renders inside it.
 */
// Drag / animation DOM handlers collide with motion's same-named props when
// the props are spread onto `motion.p`, so omit them (matching EmptyState).
export type FormMessageProps = Omit<
  React.HTMLAttributes<HTMLParagraphElement>,
  "onAnimationStart" | "onAnimationEnd" | "onDragStart" | "onDragEnd" | "onDrag"
>;

const FormMessage = React.forwardRef<HTMLParagraphElement, FormMessageProps>(
  ({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  const reduced = useReducedMotion();
  const body = error ? String(error?.message) : children;

  // Reduced-motion (or pre-resolution): instant, no transform — but still
  // honor the donor's `return null` when there's nothing to show.
  if (reduced || reduced === null) {
    if (!body) {
      return null;
    }
    return (
      <p
        ref={ref}
        id={formMessageId}
        className={cn("text-sm font-medium text-destructive", className)}
        {...props}
      >
        {body}
      </p>
    );
  }

  return (
    <AnimatePresence initial={false} mode="wait">
      {body ? (
        <motion.p
          ref={ref}
          key="form-message"
          id={formMessageId}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: durations.hover, ease: easings.smoothOut }}
          className={cn("text-sm font-medium text-destructive", className)}
          {...props}
        >
          {body}
        </motion.p>
      ) : null}
    </AnimatePresence>
  );
});
FormMessage.displayName = "FormMessage";

export {
  useFormField,
  FormProvider,
  FormFieldController,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
};
